import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Trophy, 
  Star,
  Calendar,
  Clock,
  TrendingUp,
  Users,
  Award,
  Target,
  ChevronRight,
  Loader2,
  Crown,
  Medal,
  Zap,
  RefreshCw,
  CheckCircle2,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format days remaining
const formatTimeRemaining = (days) => {
  if (days <= 0) return 'Ended';
  if (days === 1) return '1 day left';
  if (days < 7) return `${days} days left`;
  if (days < 30) return `${Math.floor(days / 7)} weeks left`;
  return `${Math.floor(days / 30)} months left`;
};

// League Status Badge
const LeagueStatusBadge = ({ status, daysRemaining }) => {
  if (status === 'active' || daysRemaining > 0) {
    return (
      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
        <div className="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse" />
        Active
      </Badge>
    );
  }
  return (
    <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">
      Completed
    </Badge>
  );
};

// Rookie League Card
const RookieLeagueCard = ({ data, onViewLeaderboard }) => {
  const progressPercent = data?.is_in_rookie 
    ? Math.max(0, 100 - (data.days_remaining / 90) * 100)
    : 100;

  return (
    <Card className="bg-gradient-to-br from-amber-950/30 to-slate-900 border-amber-500/20 hover:border-amber-500/40 transition-all" data-testid="rookie-league-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Star className="h-6 w-6 text-amber-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">Rookie League</CardTitle>
              <CardDescription className="text-amber-200/60">
                First 90 days • Ranked by Average
              </CardDescription>
            </div>
          </div>
          <LeagueStatusBadge status={data?.is_in_rookie ? 'active' : 'completed'} daysRemaining={data?.days_remaining} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {data?.is_in_rookie ? (
          <>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Progress</span>
                <span className="text-amber-400">{data.days_remaining} days remaining</span>
              </div>
              <Progress value={progressPercent} className="h-2 bg-slate-800" />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 rounded-lg bg-slate-800/50">
                <div className="text-2xl font-bold text-white">{data.average_points?.toFixed(1) || '0.0'}</div>
                <div className="text-xs text-slate-400">Avg Points</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-slate-800/50">
                <div className="text-2xl font-bold text-white">{data.weeks_played || 0}</div>
                <div className="text-xs text-slate-400">Weeks</div>
              </div>
              <div className="text-center p-3 rounded-lg bg-slate-800/50">
                <div className="text-2xl font-bold text-amber-400">#{data.rank || '-'}</div>
                <div className="text-xs text-slate-400">Rank</div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-2" />
            <p className="text-emerald-400 font-medium">Graduated!</p>
            <p className="text-sm text-slate-400">You've completed your rookie period</p>
          </div>
        )}
        
        <Button 
          variant="outline" 
          className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
          onClick={() => onViewLeaderboard('rookie')}
          data-testid="rookie-leaderboard-btn"
        >
          View Leaderboard
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

// Season League Card
const SeasonLeagueCard = ({ data, onViewLeaderboard, onEnroll }) => {
  const progressPercent = data?.is_enrolled 
    ? Math.max(0, 100 - (data.days_remaining / 90) * 100)
    : 0;

  return (
    <Card className="bg-gradient-to-br from-blue-950/30 to-slate-900 border-blue-500/20 hover:border-blue-500/40 transition-all" data-testid="season-league-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <Calendar className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">{data?.season_name || 'Season League'}</CardTitle>
              <CardDescription className="text-blue-200/60">
                3-month competition • Ranked by Total
              </CardDescription>
            </div>
          </div>
          <LeagueStatusBadge status="active" daysRemaining={data?.days_remaining} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Season Progress</span>
            <span className="text-blue-400">{formatTimeRemaining(data?.days_remaining)}</span>
          </div>
          <Progress value={progressPercent} className="h-2 bg-slate-800" />
        </div>
        
        {data?.is_enrolled ? (
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 rounded-lg bg-slate-800/50">
              <div className="text-2xl font-bold text-white">{data.total_points || 0}</div>
              <div className="text-xs text-slate-400">Total Pts</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-slate-800/50">
              <div className="text-2xl font-bold text-white">{data.weeks_played || 0}</div>
              <div className="text-xs text-slate-400">Weeks</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-slate-800/50">
              <div className="text-2xl font-bold text-blue-400">#{data.rank || '-'}</div>
              <div className="text-xs text-slate-400">Rank</div>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <Target className="h-10 w-10 text-blue-400 mx-auto mb-2" />
            <p className="text-slate-300 mb-3">Join this season's competition!</p>
            <Button 
              className="bg-blue-600 hover:bg-blue-500"
              onClick={onEnroll}
              data-testid="season-enroll-btn"
            >
              Enroll Now
            </Button>
          </div>
        )}
        
        <div className="flex items-center justify-between text-sm text-slate-400 bg-slate-800/30 rounded-lg p-3">
          <span className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {data?.total_participants || 0} participants
          </span>
          {data?.portfolio_choice && (
            <Badge variant="outline" className="text-xs">
              Portfolio: {data.portfolio_choice}
            </Badge>
          )}
        </div>
        
        <Button 
          variant="outline" 
          className="w-full border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
          onClick={() => onViewLeaderboard('season')}
          data-testid="season-leaderboard-btn"
        >
          View Leaderboard
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

// Major League Card
const MajorLeagueCard = ({ data, onViewLeaderboard }) => {
  const qualificationProgress = data?.is_enrolled 
    ? Math.min(100, (data.days_active / 90) * 100)
    : 0;

  return (
    <Card className="bg-gradient-to-br from-purple-950/30 to-slate-900 border-purple-500/20 hover:border-purple-500/40 transition-all" data-testid="major-league-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Crown className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <CardTitle className="text-lg text-white">Major League {data?.year}</CardTitle>
              <CardDescription className="text-purple-200/60">
                Annual Championship • Two Winners
              </CardDescription>
            </div>
          </div>
          {data?.is_enrolled ? (
            <LeagueStatusBadge status="active" daysRemaining={280} />
          ) : (
            <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">
              Not Enrolled
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {data?.is_enrolled ? (
          <>
            {!data.is_qualified && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Qualification Progress</span>
                  <span className="text-purple-400">{data.days_until_qualified} days to qualify</span>
                </div>
                <Progress value={qualificationProgress} className="h-2 bg-slate-800" />
              </div>
            )}
            
            {data.is_qualified && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                <span className="text-emerald-400 font-medium">Qualified for Prizes!</span>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-3 rounded-lg bg-slate-800/50">
                <div className="text-2xl font-bold text-white">{data.total_points || 0}</div>
                <div className="text-xs text-slate-400">Total Points</div>
                {data.rank_by_total && (
                  <Badge variant="outline" className="mt-1 text-xs">
                    #{data.rank_by_total} by Total
                  </Badge>
                )}
              </div>
              <div className="text-center p-3 rounded-lg bg-slate-800/50">
                <div className="text-2xl font-bold text-white">{data.average_points?.toFixed(1) || '0.0'}</div>
                <div className="text-xs text-slate-400">Avg Points</div>
                {data.rank_by_average && (
                  <Badge variant="outline" className="mt-1 text-xs">
                    #{data.rank_by_average} by Avg
                  </Badge>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <Trophy className="h-10 w-10 text-purple-400 mx-auto mb-2" />
            <p className="text-slate-300">Complete your Rookie period to join</p>
            <p className="text-sm text-slate-500 mt-1">Auto-enrollment after 90 days</p>
          </div>
        )}
        
        <div className="flex items-center justify-between text-sm text-slate-400 bg-slate-800/30 rounded-lg p-3">
          <span className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            {data?.total_participants || 0} competitors
          </span>
          <span className="flex items-center gap-2">
            <Award className="h-4 w-4 text-amber-400" />
            2 Winners
          </span>
        </div>
        
        <Button 
          variant="outline" 
          className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
          onClick={() => onViewLeaderboard('major')}
          data-testid="major-leaderboard-btn"
        >
          View Leaderboard
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </CardContent>
    </Card>
  );
};

// Leaderboard Entry Row
const LeaderboardRow = ({ entry, index, sortBy }) => {
  const getRankColor = (rank) => {
    if (rank === 1) return 'text-amber-400';
    if (rank === 2) return 'text-slate-300';
    if (rank === 3) return 'text-amber-600';
    return 'text-slate-400';
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="h-5 w-5 text-amber-400" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-slate-300" />;
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-600" />;
    return null;
  };

  return (
    <div 
      className={`flex items-center justify-between p-3 rounded-lg ${
        entry.is_you 
          ? 'bg-emerald-500/10 border border-emerald-500/30' 
          : 'bg-slate-800/30 hover:bg-slate-800/50'
      } transition-colors`}
      data-testid={`leaderboard-row-${entry.rank}`}
    >
      <div className="flex items-center gap-4">
        <div className={`w-8 text-center font-bold ${getRankColor(entry.rank)}`}>
          {getRankIcon(entry.rank) || `#${entry.rank}`}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className={`font-medium ${entry.is_you ? 'text-emerald-400' : 'text-white'}`}>
              {entry.username}
            </span>
            {entry.is_you && (
              <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">You</Badge>
            )}
            {entry.is_bot && (
              <Badge variant="outline" className="text-xs text-slate-500">Bot</Badge>
            )}
          </div>
          <div className="text-xs text-slate-500">{entry.weeks_played} weeks played</div>
        </div>
      </div>
      <div className="text-right">
        <div className="font-bold text-white">
          {sortBy === 'average' ? entry.average_points?.toFixed(1) : entry.total_points}
        </div>
        <div className="text-xs text-slate-500">
          {sortBy === 'average' ? 'avg pts' : 'total pts'}
        </div>
      </div>
    </div>
  );
};

// Main System Leagues Dashboard Component
const SystemLeaguesDashboard = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [leagueInfo, setLeagueInfo] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [majorSortBy, setMajorSortBy] = useState('total');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch public info (no auth needed)
      const infoRes = await fetch(`${API_URL}/api/system-leagues/info`);
      const infoData = await infoRes.json();
      setLeagueInfo(infoData);
      
      // Fetch user overview (auth needed)
      if (token) {
        const overviewRes = await fetch(`${API_URL}/api/system-leagues/overview`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const overviewData = await overviewRes.json();
        setOverview(overviewData);
      }
    } catch (err) {
      console.error('Error fetching system leagues:', err);
      toast.error('Failed to load league data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchLeaderboard = async (leagueType) => {
    try {
      setLeaderboardLoading(true);
      setShowLeaderboard(leagueType);
      
      let url = `${API_URL}/api/system-leagues/${leagueType}/leaderboard?limit=50`;
      if (leagueType === 'major') {
        url += `&sort_by=${majorSortBy}`;
      }
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLeaderboardData(data);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
      toast.error('Failed to load leaderboard');
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const handleEnroll = async (portfolioChoice) => {
    try {
      setEnrolling(true);
      const res = await fetch(`${API_URL}/api/system-leagues/season/enroll`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ portfolio_choice: portfolioChoice })
      });
      
      if (res.ok) {
        toast.success('Successfully enrolled in Season League!');
        setShowEnrollDialog(false);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to enroll');
      }
    } catch (err) {
      console.error('Error enrolling:', err);
      toast.error('Failed to enroll');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="system-leagues-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Trophy className="h-6 w-6 text-emerald-400" />
            System Leagues
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Compete in official leagues and climb the ranks
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          className="border-slate-700"
          onClick={fetchData}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* League Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <RookieLeagueCard 
          data={overview?.rookie}
          onViewLeaderboard={fetchLeaderboard}
        />
        <SeasonLeagueCard 
          data={overview?.season}
          onViewLeaderboard={fetchLeaderboard}
          onEnroll={() => setShowEnrollDialog(true)}
        />
        <MajorLeagueCard 
          data={overview?.major}
          onViewLeaderboard={fetchLeaderboard}
        />
      </div>

      {/* League Info Summary */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-400" />
            How System Leagues Work
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/10">
              <h4 className="font-medium text-amber-400 mb-2 flex items-center gap-2">
                <Star className="h-4 w-4" /> Rookie League
              </h4>
              <p className="text-sm text-slate-400">
                Your first 90 days. Compete on <span className="text-white font-medium">Average Points</span> — 
                consistency wins! Graduate to Major League automatically.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/10">
              <h4 className="font-medium text-blue-400 mb-2 flex items-center gap-2">
                <Calendar className="h-4 w-4" /> Season League
              </h4>
              <p className="text-sm text-slate-400">
                3-month seasons. Compete on <span className="text-white font-medium">Total Points</span>. 
                Choose to keep or reset your portfolio each season.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-purple-500/5 border border-purple-500/10">
              <h4 className="font-medium text-purple-400 mb-2 flex items-center gap-2">
                <Crown className="h-4 w-4" /> Major League
              </h4>
              <p className="text-sm text-slate-400">
                Annual championship! <span className="text-white font-medium">Two winners</span> — 
                highest Total Points AND highest Average Points.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard Dialog */}
      <Dialog open={!!showLeaderboard} onOpenChange={() => setShowLeaderboard(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trophy className="h-5 w-5 text-emerald-400" />
              {showLeaderboard === 'rookie' && 'Rookie League Leaderboard'}
              {showLeaderboard === 'season' && 'Season League Leaderboard'}
              {showLeaderboard === 'major' && 'Major League Leaderboard'}
            </DialogTitle>
            <DialogDescription>
              {showLeaderboard === 'rookie' && 'Ranked by Average Points'}
              {showLeaderboard === 'season' && 'Ranked by Total Points'}
              {showLeaderboard === 'major' && (
                <div className="flex items-center gap-2 mt-2">
                  <span>Sort by:</span>
                  <Button
                    size="sm"
                    variant={majorSortBy === 'total' ? 'default' : 'outline'}
                    className={majorSortBy === 'total' ? 'bg-purple-600' : ''}
                    onClick={() => {
                      setMajorSortBy('total');
                      fetchLeaderboard('major');
                    }}
                  >
                    Total
                  </Button>
                  <Button
                    size="sm"
                    variant={majorSortBy === 'average' ? 'default' : 'outline'}
                    className={majorSortBy === 'average' ? 'bg-purple-600' : ''}
                    onClick={() => {
                      setMajorSortBy('average');
                      fetchLeaderboard('major');
                    }}
                  >
                    Average
                  </Button>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto max-h-[50vh] space-y-2 pr-2">
            {leaderboardLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
              </div>
            ) : leaderboardData?.entries?.length > 0 ? (
              leaderboardData.entries.map((entry, index) => (
                <LeaderboardRow 
                  key={entry.user_id} 
                  entry={entry} 
                  index={index}
                  sortBy={showLeaderboard === 'rookie' ? 'average' : (showLeaderboard === 'major' ? majorSortBy : 'total')}
                />
              ))
            ) : (
              <div className="text-center py-8 text-slate-400">
                No participants yet
              </div>
            )}
          </div>
          
          {leaderboardData?.your_rank && (
            <div className="mt-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <div className="flex items-center justify-between">
                <span className="text-emerald-400">Your Position</span>
                <span className="font-bold text-white">
                  #{leaderboardData.your_rank} of {leaderboardData.total_participants}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Season Enrollment Dialog */}
      <Dialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-400" />
              Join {overview?.season?.season_name}
            </DialogTitle>
            <DialogDescription>
              Choose how to start the new season
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Button
              className="w-full h-auto p-4 flex flex-col items-start bg-slate-800 hover:bg-slate-700 border border-slate-700"
              variant="outline"
              onClick={() => handleEnroll('reset')}
              disabled={enrolling}
            >
              <div className="flex items-center gap-2 text-emerald-400 font-medium">
                <RefreshCw className="h-4 w-4" />
                Fresh Start
              </div>
              <p className="text-sm text-slate-400 mt-1 text-left">
                Reset to V$100,000 and 0 points. Compete on equal footing.
              </p>
            </Button>
            
            <Button
              className="w-full h-auto p-4 flex flex-col items-start bg-slate-800 hover:bg-slate-700 border border-slate-700"
              variant="outline"
              onClick={() => handleEnroll('keep')}
              disabled={enrolling}
            >
              <div className="flex items-center gap-2 text-blue-400 font-medium">
                <TrendingUp className="h-4 w-4" />
                Keep Portfolio
              </div>
              <p className="text-sm text-slate-400 mt-1 text-left">
                Continue with your current portfolio and points.
              </p>
            </Button>
          </div>
          
          {enrolling && (
            <div className="flex items-center justify-center py-2">
              <Loader2 className="h-5 w-5 animate-spin text-emerald-400 mr-2" />
              <span className="text-slate-400">Enrolling...</span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemLeaguesDashboard;
